import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import dayjs from 'dayjs';

interface NoteDemographicsHeaderProps {
  contract: any;
  selectedCategoryTitle?: string;
  selectedIcdCodes?: Array<{ id?: string; code: string; description?: string }>;
  selectedCptCodes?: Array<{ id?: string; code: string; description?: string }>;
  selectedHcpcsCodes?: Array<{ id?: string; code: string; description?: string }>;
}

export const NoteDemographicsHeader: React.FC<NoteDemographicsHeaderProps> = ({
  contract,
  selectedCategoryTitle = '',
  selectedIcdCodes,
  selectedCptCodes,
  selectedHcpcsCodes,
}) => {
  const isInitialEval = selectedCategoryTitle.includes('Initial');
  const isPoC = selectedCategoryTitle.includes('Plan of Care') || selectedCategoryTitle.includes('PoC');
  const isAsrSoap = selectedCategoryTitle.includes('ASR');
  const isDailyNote = selectedCategoryTitle.includes('Daily');
  const isReeval = selectedCategoryTitle.includes('Re-eval') || selectedCategoryTitle.includes('Re-cert');
  const isDischarge = selectedCategoryTitle.includes('Discharge');

  const sessionLengthValue = contract?.sessionlength || contract?.duretion || contract?.session_length || contract?.service_id?.session_length || contract?.serviceLengthMinutes;
  const formattedDob = contract?.patient?.dob && dayjs(contract.patient.dob).isValid() ? dayjs(contract.patient.dob).format('MM/DD/YY') : contract?.patient?.dob;

  let diagnosisString = contract?.diagnosis || '';
  if (selectedIcdCodes?.length || selectedCptCodes?.length || selectedHcpcsCodes?.length) {
    const icds = (selectedIcdCodes || []).map(c => c.code).join(', ');
    const cpts = (selectedCptCodes || []).map(c => c.code).join(', ');
    const hcpcs = (selectedHcpcsCodes || []).map(c => c.code).join(', ');

    const parts = [];
    if (icds) parts.push(icds);
    if (cpts) parts.push(cpts);
    if (hcpcs) parts.push(hcpcs);

    if (parts.length > 0) {
      diagnosisString = parts.join(' | ');
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        p: 4,
        mb: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          background: 'linear-gradient(90deg, #1976d2, #64b5f6)',
        }
      }}
    >
      {/* Note Title */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: '1px',
            color: 'primary.main',
            textTransform: 'uppercase',
            mb: 0.5
          }}
        >
          {selectedCategoryTitle || 'Clinical Progress Note'}
        </Typography>
        {/* <Box sx={{ width: '60px', height: '2px', bgcolor: 'primary.light', mx: 'auto' }} /> */}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {/* Left Column: Patient Info */}
        <Box>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
            Patient Information
          </Typography>
          <Stack spacing={1}>
            {[
              { label: 'Patient Name', value: contract?.patient?.name, show: true },
              { label: 'DOB', value: formattedDob, show: true },
              { label: 'Gender', value: contract?.patient?.gender, show: isInitialEval || isPoC },
              { label: 'Referring Physician', value: contract?.patient?.referringPhysician || contract?.therapist?.name || contract?.providerName, show: isInitialEval || isPoC || isReeval },
              { label: 'Insurance Info / Name', value: contract?.insuranceName || contract?.insuranceCardId?.insuranceName || contract?.patient?.insuranceInfo, show: isInitialEval || isPoC },
              { label: 'PoC Signed By Name', value: contract?.pocSignedByName, show: isPoC || isReeval },
              { label: 'PoC Signed By - License Type', value: contract?.pocSignedByLicenseType, show: isPoC || isReeval },
              { label: 'Diagnosis (ICD-10, CPT Codes,HCPCS)', value: diagnosisString, show: true },
            ].filter(item => item.show && item.value != null && String(item.value).trim() !== '').map((item, idx) => (
              <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '50% 50%', gap: 2, alignItems: 'start' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {item.label}:
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Right Column: Provider & Service Info */}
        <Box>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
            Provider Information
          </Typography>
          <Stack spacing={1}>
            {[

              { label: 'Provider Name', value: contract?.therapist?.name || contract?.providerName, show: true },
              { label: 'Provider NPI', value: contract?.therapist?.npi || contract?.providerNPI, show: isInitialEval || isPoC || isReeval || isDischarge },
              { label: 'Provider License Type / Issued State', value: contract?.therapist?.providerLicenseType + '/' + contract?.therapist?.providerState, show: true },
              { label: 'Provider License Number', value: contract?.therapist?.licenseNumber || contract?.providerLicenseNumber, show: isInitialEval || isPoC || isReeval || isDischarge },
              { label: 'Session Length', value: sessionLengthValue ? `${sessionLengthValue} min` : '', show: true },
            ].filter(item => item.show && item.value != null && String(item.value).trim() !== '').map((item, idx) => (
              <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '50% 50%', gap: 2, alignItems: 'start' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {item.label}:
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Modifier Section (if existing) */}
      {(isAsrSoap || isDailyNote) && (contract?.modifierAssistantCQCOName || contract?.modifierAssistantCQCOLicenseNumber || contract?.modifierAssistantCQCOLicenseType) && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
            Modifier (Assistant CQ/CO) Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Modifier (Assistant CQ/CO) Name:</Typography>
              <Typography variant="caption">{contract?.modifierAssistantCQCOName || '—'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Modifier (Assistant CQ/CO) License Type / Issued State:</Typography>
              <Typography variant="caption">{contract?.modifierAssistantCQCOLicenseType || '—'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Modifier (Assistant CQ/CO) License Number:</Typography>
              <Typography variant="caption">{contract?.modifierAssistantCQCOLicenseNumber || '—'}</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
